/**
 * The decisions the canvas player (ReelCanvas) takes after each drawn frame, kept apart from the player so they can be tested:
 * whether the frame is one from before a seek, and whether a looping clip goes round.
 */

/** A seek asked for from outside the player that may not have been drawn yet. `staleSeen`: the one frame that can predate it has passed. */
export interface PendingSeek { frame: number; staleSeen: boolean }

/** A drawn frame within this many frames of a seek's target is the seek's own. */
const SEEK_LANDED_WITHIN = 1;

/**
 * Whether the frame just drawn predates a seek that was asked for. The core player takes a seek at the start of its next turn,
 * so exactly one frame — the one already on its way — can still arrive after the request. That frame says nothing about where
 * the clip now is: it must not report its time, end the clip, or send a looping clip round (the player sitting on its last
 * frame, asked to go elsewhere and then to play, would otherwise start over). Returns what is still pending afterwards.
 */
export function settleSeek(pending: PendingSeek | null, frame: number, duration: number): { stale: boolean; pending: PendingSeek | null } {
  if (!pending) return { stale: false, pending: null };
  const target = Math.max(0, duration > 0 ? Math.min(duration, pending.frame) : pending.frame);
  if (pending.staleSeen || Math.abs(frame - target) <= SEEK_LANDED_WITHIN) return { stale: false, pending: null };
  return { stale: true, pending: { ...pending, staleSeen: true } };
}

/** Whether a clip that is playing on loop has drawn its last frame and goes back to its first. */
export function shouldLoop(s: { playing: boolean; looping: boolean; finished: boolean; frame: number; duration: number }): boolean {
  return s.playing && s.looping && s.duration > 0 && (s.finished || s.frame >= s.duration);
}
